import React, { useEffect, useReducer } from "react";
import { Form, Row, Col, Container, FormGroup, Spinner } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from "../lib/components/LoadingSpinner";
import { Pager } from "../lib/components/Pager";
import {
  CaretUpFill as CaretUp,
  CaretDownFill as CaretDown,
  Download,
  Search,
} from "react-bootstrap-icons";
import { withHeaderMessages } from "../higherOrderComponents/WithHeaderMessages";
import qs from "qs";

const ContractSearch = (props) => {
  const location = useLocation();
  const { searchTerm, page, pageSize } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });
  const navigate = useNavigate();

  const initialState = {
    searchTerm: searchTerm || "",
    page: page * 1 || 1,
    pageSize: pageSize * 1 || 10,
    results: [],
    maxItems: 0,
    maxPages: 1,
    isLoading: false,
    orderBy: "contractnumber",
    orderByDesc: false,
    selectedContracts: [],
    statusFilter: "",
    acceptedStartDate: "",
    acceptedEndDate: "",
    disbursementStartDate: "",
    disbursementEndDate: "",
    notationText: "",
    hasNotations: false,
    isFetchingExportData: false,
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case "searchTerm":
        return {
          ...state,
          searchTerm: action.payload,
        };
      case "page":
        return {
          ...state,
          page: action.payload,
        };
      case "pageSize":
        return {
          ...state,
          pageSize: action.payload,
        };
      case "results":
        return {
          ...state,
          results: action.payload,
        };
      case "maxItems":
        return {
          ...state,
          maxItems: action.payload,
        };
      case "maxPages":
        return {
          ...state,
          maxPages: action.payload,
        };
      case "orderBy":
        return {
          ...state,
          orderBy: action.payload,
        };
      case "orderByDesc":
        return {
          ...state,
          orderByDesc: action.payload,
        };
      case "selectedContracts":
        return {
          ...state,
          selectedContracts: action.payload,
        };
      case "statusFilter":
        return {
          ...state,
          statusFilter: action.payload,
        };
      case "acceptedStartDate":
        return {
          ...state,
          acceptedStartDate: action.payload,
        };
      case "acceptedEndDate":
        return {
          ...state,
          acceptedEndDate: action.payload,
        };
      case "disbursementStartDate":
        return {
          ...state,
          disbursementStartDate: action.payload,
        };
      case "disbursementEndDate":
        return {
          ...state,
          disbursementEndDate: action.payload,
        };
      case "notationText":
        return {
          ...state,
          notationText: action.payload,
        };
      case "hasNotations":
        return {
          ...state,
          hasNotations: action.payload,
        };
      case "isLoading":
        return {
          ...state,
          isLoading: action.payload,
        };
      case "isFetchingExportData":
        return {
          ...state,
          isFetchingExportData: action.payload,
        };
      case "hideLoader":
        return {
          ...state,
          isLoading: false,
        };
      default:
        throw new Error();
    }
  };

  const [contractSearchState, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const statusFromUrl = params.get("status") || "";
    const acceptedStartDateFromUrl = params.get("acceptedStartDate") || "";
    const acceptedEndDateFromUrl = params.get("acceptedEndDate") || "";
    const disbursementStartDateFromUrl =
      params.get("disbursementStartDate") || "";
    const disbursementEndDateFromUrl = params.get("disbursementEndDate") || "";
    const notationTextFromUrl = params.get("notationText") || "";
    const searchTermFromUrl = params.get("searchTerm") || "";
    const pageFromUrl = parseInt(params.get("page"), 10) || 1;
    const pageSizeFromUrl = parseInt(params.get("pageSize"), 10) || 10;
    const hasNotationsFromUrl = params.get("hasNotations") === "true";
    const orderByFromUrl = params.get("orderBy") || contractSearchState.orderBy;
    const orderByDescFromUrl = params.get("orderByDesc") === "true";

    // Sync state to the URL unconditionally, so removed params reset too.
    // (These are for your controlled inputs — NOT what drives the search below.)
    dispatch({ type: "searchTerm", payload: searchTermFromUrl });
    dispatch({ type: "page", payload: pageFromUrl });
    dispatch({ type: "pageSize", payload: pageSizeFromUrl });
    dispatch({ type: "statusFilter", payload: statusFromUrl });
    dispatch({ type: "acceptedStartDate", payload: acceptedStartDateFromUrl });
    dispatch({ type: "acceptedEndDate", payload: acceptedEndDateFromUrl });
    dispatch({
      type: "disbursementStartDate",
      payload: disbursementStartDateFromUrl,
    });
    dispatch({
      type: "disbursementEndDate",
      payload: disbursementEndDateFromUrl,
    });
    dispatch({ type: "notationText", payload: notationTextFromUrl });
    dispatch({ type: "hasNotations", payload: hasNotationsFromUrl });
    dispatch({ type: "orderBy", payload: orderByFromUrl });
    dispatch({ type: "orderByDesc", payload: orderByDescFromUrl });

    props.header.setHeader("contractSearch");

    // Search from the URL values directly — never from just-dispatched state.
    search(
      searchTermFromUrl,
      pageFromUrl,
      pageSizeFromUrl,
      orderByFromUrl,
      orderByDescFromUrl,
      statusFromUrl,
      acceptedStartDateFromUrl,
      acceptedEndDateFromUrl,
      disbursementStartDateFromUrl,
      disbursementEndDateFromUrl,
      notationTextFromUrl,
      hasNotationsFromUrl,
      orderByFromUrl,
      orderByDescFromUrl,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSearchChanged = (event) => {
    dispatch({ type: "searchTerm", payload: event.target.value });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const { searchTerm, pageSize } = contractSearchState;
    dispatch({ type: "page", payload: 1 });
    searchAndSetHistory(searchTerm, 1, pageSize);
  };

  const pagingChangeHandler = (page, pageSize) => {
    const { searchTerm } = contractSearchState;

    dispatch({ type: "page", payload: page });
    dispatch({ type: "pageSize", payload: pageSize });
    searchAndSetHistory(searchTerm, page, pageSize);
  };

  const searchAndSetHistory = (searchTerm, page, pageSize) => {
    const params = qs.parse(location.search, { ignoreQueryPrefix: true });
    navigate(
      location.pathname +
        qs.stringify(
          { ...params, searchTerm: searchTerm, page: page, pageSize: pageSize },
          { addQueryPrefix: true },
        ),
    );
  };

  const search = async (
    searchTerm,
    page,
    pageSize,
    orderBy,
    orderByDesc,
    statusFilter,
    acceptedStartDate,
    acceptedEndDate,
    disbursementStartDate,
    disbursementEndDate,
    notationText,
    hasNotations,
  ) => {
    const searchPayload = {
      Query: searchTerm,
      Options: {
        Page: page,
        ResultsPerPage: pageSize,
        Sort: {
          Name: orderBy,
          Descending: orderByDesc,
        },
      },
      Status: statusFilter || undefined,
      AcceptedStartDate: acceptedStartDate || undefined,
      AcceptedEndDate: acceptedEndDate || undefined,
      DisbursementStartDate: disbursementStartDate || undefined,
      DisbursementEndDate: disbursementEndDate || undefined,
      NotationText: notationText || undefined,
      HasNotations: hasNotations || undefined,
    };

    const searchQueryString = qs.stringify(searchPayload, {
      allowDots: true,
      encode: false,
      addQueryPrefix: true,
    });

    dispatch({ type: "isLoading", payload: true });

    try {
      const response = await props.api.get(
        "/api/contracts/search" + searchQueryString,
      );
      if (response.status === 200) {
        const data = response.data;
        dispatch({
          type: "results",
          payload: data.results,
          maxItems: data.totalRecords,
          maxPages: data.totalPages,
        });
        dispatch({ type: "maxItems", payload: data.totalRecords });
        dispatch({ type: "maxPages", payload: data.totalPages });
      }
    } catch (err) {
      console.error("Contract search failed", err);
    } finally {
      dispatch({ type: "isLoading", payload: false });
    }
  };

  const handleSortResults = (newOrderBy) => {
    const { orderBy, orderByDesc } = contractSearchState;
    const newOrderByDesc = orderBy === newOrderBy ? !orderByDesc : false;

    const params = qs.parse(location.search, { ignoreQueryPrefix: true });

    navigate(
      location.pathname +
        qs.stringify(
          { ...params, orderBy: newOrderBy, orderByDesc: newOrderByDesc },
          { addQueryPrefix: true },
        ),
    );
  };

  const viewContractsIsDisabled = () => {
    return contractSearchState.selectedContracts.length === 0;
  };

  const viewContracts = () => {
    navigate(
      "/contracts-combined?n=" +
        contractSearchState.selectedContracts.join(","),
    );
  };

  const handleContractSelected = (contract, event) => {
    const { selectedContracts } = contractSearchState;
    const index = selectedContracts.indexOf(contract.number);
    if (index > -1) {
      selectedContracts.splice(index, 1);
    } else {
      selectedContracts.splice(0, 0, contract.number);
    }
    dispatch({ type: "selectedContracts", payload: selectedContracts });
  };

  const handleDownload = async () => {
    const {
      searchTerm,
      orderBy,
      orderByDesc,
      statusFilter,
      acceptedStartDate,
      acceptedEndDate,
      disbursementStartDate,
      disbursementEndDate,
      notationText,
      hasNotations,
    } = contractSearchState;

    const searchPayload = {
      Query: searchTerm,
      Options: {
        Sort: {
          Name: orderBy,
          Descending: orderByDesc,
        },
      },
      Status: statusFilter || undefined,
      AcceptedStartDate: acceptedStartDate || undefined,
      AcceptedEndDate: acceptedEndDate || undefined,
      DisbursementStartDate: disbursementStartDate || undefined,
      DisbursementEndDate: disbursementEndDate || undefined,
      NotationText: notationText || undefined,
      HasNotations: hasNotations || undefined,
    };

    const queryString = qs.stringify(searchPayload, {
      allowDots: true,
      encode: false,
      addQueryPrefix: true,
    });

    try {
      dispatch({ type: "isFetchingExportData", payload: true });
      const response = await props.api.get(
        "/api/contracts/search/export" + queryString,
        { responseType: "blob" },
      );
      let filename = "contracts.csv"; // fallback
      const disposition = response.headers["content-disposition"];
      if (disposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      dispatch({ type: "isFetchingExportData", payload: false });
    } catch (err) {
      console.error("Download failed", err);
      dispatch({ type: "isFetchingExportData", payload: false });
    }
  };

  const order = `${contractSearchState.orderBy}_${contractSearchState.orderByDesc ? "desc" : "asc"}`;
  const isFetchingExportData = contractSearchState.isFetchingExportData;
  return (
    <Container>
      <div className="cp-contracts__toolbar">
        <form className="cp-contracts__search-form" onSubmit={handleSubmit}>
          <div className="cp-contracts__search">
            <Search className="cp-contracts__search-icon" aria-hidden="true" />
            <input
              type="text"
              name="searchCriteria"
              onChange={handleSearchChanged}
              className="cp-contracts__input"
              placeholder="Search by Name or Contract Number..."
            />
          </div>

          <button
            type="submit"
            className="cp-contracts__search-btn"
            disabled={contractSearchState.isLoading}
          >
            Search
          </button>
        </form>

        <div className="cp-contracts__spacer" aria-hidden="true" />
        {isFetchingExportData ? (
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        ) : (
          <button
            type="button"
            className="cp-contracts__export"
            onClick={handleDownload}
            disabled={
              contractSearchState.isLoading ||
              !contractSearchState.results?.length
            }
          >
            <Download />
            Export
          </button>
        )}
      </div>

      {contractSearchState.isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Row className="section">
            <Col md={12}>
              <div className="scroll-table">
                <table responsive className="table">
                  <thead>
                    <tr>
                      <th
                        className="sortable-header"
                        onClick={() =>
                          handleSortResults("providercontractnumber")
                        }
                      >
                        <div className="sortable-header-text">
                          Provider Contract #
                        </div>
                        <div>
                          <span
                            style={{
                              opacity:
                                order === "providercontractnumber_asc" ? 1 : "",
                            }}
                          >
                            <CaretUp />
                          </span>
                          <span
                            style={{
                              opacity:
                                order === "providercontractnumber_desc"
                                  ? 1
                                  : "",
                            }}
                          >
                            <CaretDown />
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={() => handleSortResults("contractnumber")}
                      >
                        <div className="sortable-header-text">
                          ClearPoint Contract #
                        </div>
                        <div>
                          <span
                            style={{
                              opacity: order === "contractnumber_asc" ? 1 : "",
                            }}
                          >
                            <CaretUp />
                          </span>
                          <span
                            style={{
                              opacity: order === "contractnumber_desc" ? 1 : "",
                            }}
                          >
                            <CaretDown />
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={() => handleSortResults("provider")}
                      >
                        <div className="sortable-header-text">Provider</div>
                        <div>
                          <span
                            style={{
                              opacity: order === "provider_asc" ? 1 : "",
                            }}
                          >
                            <CaretUp />
                          </span>
                          <span
                            style={{
                              opacity: order === "provider_desc" ? 1 : "",
                            }}
                          >
                            <CaretDown />
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={() => handleSortResults("recipient")}
                      >
                        <div className="sortable-header-text">Recipients</div>
                        <div>
                          <span
                            style={{
                              opacity: order === "recipient_asc" ? 1 : "",
                            }}
                          >
                            <CaretUp />
                          </span>
                          <span
                            style={{
                              opacity: order === "recipient_desc" ? 1 : "",
                            }}
                          >
                            <CaretDown />
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={() => handleSortResults("purchaser")}
                      >
                        <div className="sortable-header-text">Purchaser</div>
                        <div>
                          <span
                            style={{
                              opacity: order === "purchaser_asc" ? 1 : "",
                            }}
                          >
                            <CaretUp />
                          </span>
                          <span
                            style={{
                              opacity: order === "purchaser_desc" ? 1 : "",
                            }}
                          >
                            <CaretDown />
                          </span>
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        onClick={() => handleSortResults("status")}
                      >
                        <div className="sortable-header-text">Status</div>
                        <div>
                          <span
                            style={{ opacity: order === "status_asc" ? 1 : "" }}
                          >
                            <CaretUp />
                          </span>
                          <span
                            style={{
                              opacity: order === "status_desc" ? 1 : "",
                            }}
                          >
                            <CaretDown />
                          </span>
                        </div>
                      </th>
                      <th>&nbsp;</th>
                      <th>Select</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(contractSearchState?.results || []).map((r) => (
                      <tr
                        key={r.number}
                        className={r.hasNotes ? "color-red" : ""}
                      >
                        <td>{r.providerNumber}</td>
                        <td>{r.number}</td>
                        <td>{r.provider ? r.provider.name : ""}</td>
                        <td>
                          {r.recipients
                            .map((rec) => `${rec.firstName} ${rec.lastName}`)
                            .join(", ")}
                        </td>
                        <td>{`${r.purchaser.firstName} ${r.purchaser.lastName}`}</td>
                        <td>{r.status}</td>
                        <td>
                          <Link
                            to={`/contracts/${r.number}`}
                            className="contractDetails"
                          >
                            View Details
                          </Link>
                        </td>
                        <td className="text-center">
                          <FormGroup controlId="formBasicCheckbox">
                            <Form.Check
                              name={r.number}
                              type="checkbox"
                              onChange={(event) =>
                                handleContractSelected(r, event)
                              }
                              style={{
                                padding: "0",
                                margin: "0",
                                minHeight: "15px",
                              }}
                            />
                          </FormGroup>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Col className="text-end">
                  <button
                    className="btn btn-sec"
                    disabled={viewContractsIsDisabled()}
                    onClick={viewContracts}
                  >
                    View Selected Contracts
                  </button>
                </Col>
              </div>
            </Col>
          </Row>
          <Pager
            maxPages={contractSearchState.maxPages}
            maxItems={contractSearchState.maxItems}
            pageSize={contractSearchState.pageSize}
            page={contractSearchState.page}
            onPageChange={pagingChangeHandler}
            onPageSizeChange={pagingChangeHandler}
          />
        </>
      )}
    </Container>
  );
};

export default withHeaderMessages(WithApiAccess(ContractSearch));
